import { useState, useEffect } from "react";
import "@/App.css";
import { Phone, Menu, X, Wrench, Car, Clock, ShieldCheck, Star, MapPin, ChevronRight, ChevronDown, Zap, MessageCircle, Mail, Battery, Settings, AlertTriangle, CheckCircle, Truck, Gauge } from "lucide-react";
import { Toaster, toast } from "sonner";
import axios from "axios";
import ServiceAreaMap from "./components/ServiceAreaMap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE_NUMBER = "07874040920";
const WHATSAPP_NUMBER = "447874040920";
const GOOGLE_REVIEWS_URL = "https://www.google.com/maps/place/Fazeli's+Autos/@53.4166993,-3.3638763,9z/data=!4m8!3m7!1s0xa71ab582c30a2149:0x275d911d6b7b8fae!8m2!3d53.4197968!4d-2.7077904!9m1!1b1!16s%2Fg%2F11x_6_fnlg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_2c795b09-e720-4b6f-8909-3e642ad8804c/artifacts/0xbeev9r_FullSizeRender%20no%20pattern.png";

// Business Images
const BUSINESS_IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_emergency-mechanic-3/artifacts/betzmwve_IMG_8191.jpeg",
  mechanic1: "https://customer-assets.emergentagent.com/job_emergency-mechanic-3/artifacts/j0ehdqju_IMG_8192.jpeg",
  mechanic2: "https://customer-assets.emergentagent.com/job_emergency-mechanic-3/artifacts/94wo8dom_IMG_8193.jpeg",
  mechanic3: "https://customer-assets.emergentagent.com/job_emergency-mechanic-3/artifacts/8itpnwj2_IMG_8194.jpeg",
  wheelChange: "https://customer-assets.emergentagent.com/job_2c795b09-e720-4b6f-8909-3e642ad8804c/artifacts/cnrakiz6_IMG_8359.jpeg",
  recovery: "https://customer-assets.emergentagent.com/job_2c795b09-e720-4b6f-8909-3e642ad8804c/artifacts/ivfc56c5_IMG_8360.jpeg",
};

// Google "G" logo icon
const GoogleIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.4l6.2 5.2C41.4 35.6 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z"/>
  </svg>
);

const NAV_LINKS = [
  { name: "Home", id: "home" },
  { name: "Services", id: "services" },
  { name: "About", id: "about" },
  { name: "Reviews", id: "reviews" },
  { name: "Contact", id: "contact" },
];

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    NAV_LINKS.forEach((link) => {
      const el = document.getElementById(link.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleNavClick = (id) => {
    setIsMobileMenuOpen(false);
    scrollToSection(id);
  };

  return (
    <>
      <nav className={`navbar ${isScrolled ? "scrolled" : ""}`} data-testid="navbar">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => handleNavClick("home")} className="flex items-center" data-testid="logo-link">
              <img src={LOGO_URL} alt="Fazeli's Autos" className="h-14 w-auto" />
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.name}
                  onClick={() => handleNavClick(link.id)}
                  className={`text-sm font-semibold uppercase tracking-wider transition-colors hover:text-white ${
                    activeSection === link.id ? "text-white" : "text-[#B8C4B6]"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </button>
              ))}
              <a href={`tel:${PHONE_NUMBER}`} className="flex items-center gap-2 bg-[#FCF4DD] text-[#1E3F2D] font-bold uppercase tracking-wider px-6 py-3 rounded-lg hover:bg-[#F5EBC8] transition-colors" style={{ fontFamily: "'Barlow Condensed', sans-serif" }} data-testid="nav-call-btn">
                <Phone size={18} />
                Call Now
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? "open" : ""}`} data-testid="mobile-menu">
        <button className="absolute top-6 right-6 text-[#1E3F2D]" onClick={() => setIsMobileMenuOpen(false)}>
          <X size={32} />
        </button>
        {NAV_LINKS.map((link) => (
          <button
            key={link.name}
            onClick={() => handleNavClick(link.id)}
            className={activeSection === link.id ? "text-[#1E3F2D]" : "text-[#4B5563]"}
            data-testid={`mobile-nav-${link.name.toLowerCase()}`}
          >
            {link.name}
          </button>
        ))}
        <a href={`tel:${PHONE_NUMBER}`} className="btn-primary mt-8 flex items-center gap-2" data-testid="mobile-call-btn">
          <Phone size={20} />
          Call Now
        </a>
      </div>
    </>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="footer py-16" data-testid="footer">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div>
            <img src={LOGO_URL} alt="Fazeli's Autos" className="h-14 w-auto rounded-md mb-5" />
            <p className="text-[#B8C4B6] text-sm">
              Professional mobile mechanic services across the North West. Available 24/7 for all your automotive needs.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-bold uppercase mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Quick Links</h4>
            <div className="flex flex-col gap-2 items-start">
              <button onClick={() => scrollToSection("services")} className="text-[#B8C4B6] hover:text-white transition-colors">Services</button>
              <button onClick={() => scrollToSection("about")} className="text-[#B8C4B6] hover:text-white transition-colors">About Us</button>
              <button onClick={() => scrollToSection("reviews")} className="text-[#B8C4B6] hover:text-white transition-colors">Reviews</button>
              <button onClick={() => scrollToSection("contact")} className="text-[#B8C4B6] hover:text-white transition-colors">Contact</button>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-bold uppercase mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Services</h4>
            <div className="flex flex-col gap-2">
              <span className="text-[#B8C4B6]">Diagnostics & Fault Finding</span>
              <span className="text-[#B8C4B6]">Auto Electrical Repairs</span>
              <span className="text-[#B8C4B6]">Vehicle Servicing</span>
              <span className="text-[#B8C4B6]">Breakdown & Recovery</span>
              <span className="text-[#B8C4B6]">Vehicle Transport</span>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-bold uppercase mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Contact</h4>
            <div className="flex flex-col gap-3">
              <a href={`tel:${PHONE_NUMBER}`} className="flex items-center gap-2 text-[#B8C4B6] hover:text-white transition-colors">
                <Phone size={18} />
                {PHONE_NUMBER}
              </a>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#B8C4B6] hover:text-[#25D366] transition-colors">
                <MessageCircle size={18} />
                WhatsApp Us
              </a>
              <span className="flex items-center gap-2 text-[#B8C4B6]">
                <Clock size={18} />
                Open 24 Hours
              </span>
            </div>
          </div>
        </div>

        <div className="border-t border-[#2E4A38] mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#8DA38A] text-sm">© 2026 Fazeli's Auto Works. All rights reserved.</p>
          <p className="text-[#8DA38A] text-sm">Mobile Mechanic | North West England & North Wales</p>
        </div>
      </div>
    </footer>
  );
};

// Sticky Call + WhatsApp Buttons Component
const StickyButtons = () => (
  <div className="sticky-contact" data-testid="sticky-contact">
    <a
      href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I need help with my vehicle.`}
      target="_blank"
      rel="noopener noreferrer"
      className="sticky-btn sticky-btn-wa"
      data-testid="sticky-whatsapp-btn"
    >
      <MessageCircle size={20} />
      <span className="hidden sm:inline">WhatsApp</span>
    </a>
    <a href={`tel:${PHONE_NUMBER}`} className="sticky-btn sticky-btn-call" data-testid="sticky-call-btn">
      <Phone size={20} />
      <span className="hidden sm:inline">Call Now</span>
    </a>
  </div>
);

// ===== Sections =====

const HeroSection = () => (
  <section id="home" className="hero scroll-mt-20" data-testid="hero-section">
    <div className="hero-bg" style={{ backgroundImage: `url('${BUSINESS_IMAGES.hero}')` }} />
    <div className="hero-overlay absolute inset-0" />
    <div className="hero-content container mx-auto px-4 lg:px-8 py-8 md:py-28">
      <div className="max-w-3xl">
        <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#4F7A5F] mb-4">
          Mobile Mechanic • Auto Electrician • Vehicle Recovery
        </p>
        <h1 className="text-[2.5rem] leading-[1.05] md:text-7xl md:leading-tight font-black tracking-normal uppercase text-[#1E3F2D] mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          24/7 Mobile Mechanic – Fast, Reliable, and Affordable
        </h1>
        <p className="text-lg md:text-xl text-[#374151] mb-8 max-w-2xl">
          We come to you. Same-day diagnostics, servicing, repairs and roadside recovery across the North West & North Wales. Fast, honest and fully insured.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="hero-trust-row">
          {["Fully Insured", "Own Recovery Truck", "24/7 Availability", "Free Quotes"].map((item) => (
            <div key={item} className="flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-[#DCE3DB] rounded-full px-4 py-2 text-[#1E3F2D] text-sm font-semibold shadow-sm" data-testid={`trust-item-${item.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <CheckCircle size={16} className="text-[#1E3F2D]" />
              {item}
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <a href={`tel:${PHONE_NUMBER}`} className="btn-primary flex items-center justify-center gap-2" data-testid="hero-call-btn">
            <Phone size={20} />
            Call Now
          </a>
          <button onClick={() => scrollToSection("contact")} className="btn-outline flex items-center justify-center gap-2" data-testid="hero-quote-btn">
            Get a Quote
            <ChevronRight size={20} />
          </button>
        </div>

        <a
          href={GOOGLE_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 mt-8 bg-white border border-[#E5E7E6] rounded-full pl-4 pr-5 py-2.5 shadow-sm hover:shadow-md hover:border-[#C8D1CC] transition-all"
          data-testid="hero-google-badge"
        >
          <GoogleIcon size={22} />
          <div className="flex flex-col leading-tight">
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-[#111827] text-sm">4.8</span>
              <span className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="star-gold fill-current" size={13} />
                ))}
              </span>
            </div>
            <span className="text-xs text-[#6B7280]">Rated by 190+ customers on Google</span>
          </div>
        </a>
      </div>
    </div>
  </section>
);

const BenefitsSection = () => {
  const benefits = [
    { icon: Clock, title: "24/7 Service", desc: "Available round the clock" },
    { icon: Car, title: "Mobile Service", desc: "We come to you" },
    { icon: Zap, title: "Fast Response", desc: "Quick arrival times" },
    { icon: Star, title: "Trusted", desc: "4.8★ from 190+ reviews" },
    { icon: ShieldCheck, title: "Fully Insured", desc: "Insured & professional" },
  ];
  return (
    <section className="section bg-[#FBF6E9] tools-pattern" data-testid="benefits-section">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 -mx-4 px-4 hide-scrollbar md:mx-0 md:px-0 md:pb-0 md:grid md:grid-cols-3 lg:grid-cols-5 md:gap-6 md:overflow-visible">
          {benefits.map((benefit, index) => (
            <div key={index} className="benefit-card flex-shrink-0 w-[42%] snap-start md:w-auto" data-testid={`benefit-${index}`}>
              <div className="benefit-icon"><benefit.icon /></div>
              <h3 className="text-xl font-bold uppercase mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{benefit.title}</h3>
              <p className="text-[#6B7280] text-sm">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ServicesSection = () => {
  const services = [
    { icon: Settings, title: "Diagnostics & Fault Finding", description: "Advanced computer diagnostics to identify issues quickly and accurately. We use the latest equipment to read fault codes and pinpoint problems." },
    { icon: Zap, title: "Auto Electrical Repairs", description: "Expert auto electrician services — alternators, starters, wiring, lights, fault finding and electronic components." },
    { icon: Wrench, title: "Engine Repairs", description: "Complete engine diagnostics and repairs. From minor fixes to major overhauls, we've got you covered." },
    { icon: Gauge, title: "Vehicle Servicing", description: "Full and interim servicing to keep your vehicle running smoothly and reliably — carried out at your home or workplace." },
    { icon: Battery, title: "Battery Replacement", description: "Battery testing, supply, and fitting. We stock a range of quality batteries and can have you back on the road quickly." },
    { icon: Car, title: "Brake & Suspension", description: "Brake pads, discs, calipers, and full suspension work. Ensuring your safety is our top priority." },
    { icon: AlertTriangle, title: "Breakdown & Recovery", description: "24/7 emergency breakdown assistance and vehicle recovery. If we can't fix it at the roadside, we'll safely recover your vehicle to your home or garage." },
    { icon: Truck, title: "Vehicle Transport", description: "Need your vehicle moved? We provide vehicle transport with our own recovery truck — from driveway collections to relocating your car wherever it needs to go." },
    { icon: Clock, title: "Same-Day Repairs", description: "Most repairs completed the same day. We understand your time is valuable and aim to minimise disruption." },
    { icon: MapPin, title: "Mobile Service", description: "We come to your home, workplace, or roadside location. No need to arrange transport or lose your day." },
  ];
  return (
    <section id="services" className="section bg-[#F2F3F0] scroll-mt-20" data-testid="services-section">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#4F7A5F] mb-4">What We Do</p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-normal uppercase text-[#1E3F2D] mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Professional Auto Services
          </h2>
          <p className="text-[#4B5563] max-w-2xl mx-auto">
            Looking for a mobile mechanic near you? From emergency breakdowns to routine servicing, we provide expert mobile car repairs, auto electrical work and vehicle recovery in Liverpool, Manchester, Preston, Chester, Warrington, Blackburn, Wirral, Wrexham and right across the North West of England & North Wales — all at your location.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div key={index} className="service-card" data-testid={`service-card-${index}`}>
              <service.icon className="text-[#1E3F2D] mb-6" size={40} />
              <h3 className="text-2xl font-bold uppercase mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{service.title}</h3>
              <p className="text-[#4B5563]">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutSection = () => {
  const values = [
    { icon: ShieldCheck, title: "Honest & Transparent", desc: "No hidden fees, clear communication about all work needed." },
    { icon: Star, title: "Quality Workmanship", desc: "Every job done right the first time with quality parts." },
    { icon: Clock, title: "Reliable & Punctual", desc: "We value your time and always arrive when promised." },
    { icon: CheckCircle, title: "Customer Focused", desc: "Your satisfaction is our top priority on every job." },
  ];
  return (
    <>
      <section id="about" className="section bg-white scroll-mt-20" data-testid="about-section">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="lg:w-1/2">
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#4F7A5F] mb-4">About Us</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-normal uppercase text-[#1E3F2D] mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                Your Trusted Mobile Mechanic
              </h2>
              <p className="text-lg text-[#4B5563] mb-6">
                Fazeli's Autos provides professional mobile mechanic, auto electrician and vehicle recovery services across the North West and North Wales. Whether you've broken down, need vehicle transport, diagnostics, servicing, repairs or electrical fault finding, we come directly to you.
              </p>
              <p className="text-lg text-[#4B5563]">
                We specialise in advanced diagnostics, auto electrical repairs, batteries, brakes, suspension, roadside recovery and vehicle transport. No need to visit a garage — we bring the experience, equipment and recovery truck to your home, workplace or roadside. Fast, honest, fully insured and professional. Covering Liverpool, Manchester, Chester, Warrington and surrounding areas, with a 4.8★ rating from 190+ customers.
              </p>
            </div>
            <div className="lg:w-1/2">
              <img src={BUSINESS_IMAGES.mechanic3} alt="Fazeli's Auto Works - Mobile Mechanic" className="w-full h-[75vh] md:h-[450px] object-cover rounded-lg border-4 border-[#1E3F2D]" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section bg-[#F2F3F0] tools-pattern" data-testid="values-section">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#4F7A5F] mb-4">Our Values</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-normal uppercase text-[#1E3F2D]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              What Sets Us Apart
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="benefit-card" data-testid={`value-${index}`}>
                <div className="benefit-icon"><value.icon /></div>
                <h3 className="text-xl font-bold uppercase mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{value.title}</h3>
                <p className="text-[#6B7280] text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section bg-white" data-testid="why-choose-section">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12">
            <div className="lg:w-1/2">
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#4F7A5F] mb-4">Why Choose Us</p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-normal uppercase text-[#1E3F2D] mb-8" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                Experience You Can Trust
              </h2>
              <div className="space-y-6">
                <div className="border-accent-left pl-6">
                  <h4 className="text-xl font-bold text-[#1E3F2D] mb-2">24/7 Availability</h4>
                  <p className="text-[#4B5563]">Car troubles don't follow a 9-5 schedule, and neither do we. We're here when you need us.</p>
                </div>
                <div className="border-accent-left pl-6">
                  <h4 className="text-xl font-bold text-[#1E3F2D] mb-2">Mobile Service</h4>
                  <p className="text-[#4B5563]">Save time and hassle – we come to your home, work, or roadside location.</p>
                </div>
                <div className="border-accent-left pl-6">
                  <h4 className="text-xl font-bold text-[#1E3F2D] mb-2">Fair Pricing</h4>
                  <p className="text-[#4B5563]">Competitive rates with no hidden surprises. We quote before we work.</p>
                </div>
                <div className="border-accent-left pl-6">
                  <h4 className="text-xl font-bold text-[#1E3F2D] mb-2">Experienced Technicians</h4>
                  <p className="text-[#4B5563]">Skilled mechanics with expertise across all makes and models.</p>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2">
              <img src={BUSINESS_IMAGES.mechanic2} alt="Fazeli's Auto Works - Professional Service" className="w-full h-full object-cover min-h-[400px] rounded-lg" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const GallerySection = () => (
  <section className="section bg-[#F2F3F0]" data-testid="gallery-section">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="text-center mb-12">
        <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#4F7A5F] mb-4">Our Work</p>
        <h2 className="text-4xl md:text-5xl font-bold tracking-normal uppercase text-[#1E3F2D]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          See Us In Action
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[BUSINESS_IMAGES.hero, BUSINESS_IMAGES.wheelChange, BUSINESS_IMAGES.mechanic1, BUSINESS_IMAGES.recovery, BUSINESS_IMAGES.mechanic2, BUSINESS_IMAGES.mechanic3].map((img, index) => (
          <div key={index} className="gallery-item" data-testid={`gallery-item-${index}`}>
            <img src={img} alt={`Fazeli's Auto Works - Work Photo ${index + 1}`} />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ReviewsSection = () => {
  const reviews = [
    { name: "Sean Taylor", text: "What an amazing guy — many thanks for your speedy response and great service. I like the way you keep your customers updated, great prices, not overly expensive and very knowledgeable on your work. I'm definitely looking to book you back for some more work, even the wife's car. Overall very happy and I'd 100% recommend you to family and friends.", rating: 5, highlight: "Highly Recommended" },
    { name: "Joe O'Connor", text: "Came on a Sunday morning and fixed the problem in no time — car's back to running how it should. He's on the dot for time, honest and transparent, and takes his time to make sure the job is done correctly. He registered my new battery correctly on his diagnostic computer and fixed my DPF issues. The car feels powerful and like new again. If you want a mechanic who is honest, reliable and knows what he's doing, this is the guy!", rating: 5, highlight: "Punctual & Honest" },
    { name: "James Tommo", text: "Proper decent lad. Got to work straight away and will definitely get onto him again. He knows what he's doing.", rating: 5, highlight: "Knows His Stuff" },
    { name: "Donald Bennett", text: "Came to my home and even in pouring rain sorted my window problem. Very pleased with the result and would happily recommend him.", rating: 5, highlight: "Above & Beyond" },
    { name: "Ayaz Ghafur", text: "Had a problem with the coolant level and went to a few garages — they couldn't find the exact problem and said it was either the radiator or water pump. I booked in with Fazeli instead and he found the problem instantly and fixed it, with no need to change the radiator or water pump. Great service.", rating: 5, highlight: "Accurate Diagnosis" },
    { name: "Szebasztian Sztojka", text: "I recently used Fazeli for a DPF and battery issue on my BMW 5 Series, and I couldn't be happier with the service.", rating: 5, highlight: "DPF & Battery" },
    { name: "James Mitchell", text: "Brilliant service! Called at 10pm with a breakdown and they were out within the hour. Fixed the issue on the spot and had me back on the road. Very professional and friendly.", rating: 5, highlight: "Quick Response" },
    { name: "Sarah Khan", text: "Honest and reliable. Told me exactly what was wrong, fixed it efficiently, and didn't try to upsell unnecessary work. Will definitely use again.", rating: 5, highlight: "Honest Pricing" },
    { name: "David Thompson", text: "Excellent communication throughout. Kept me informed at every step and explained everything clearly. Fair prices and great workmanship.", rating: 5, highlight: "Clear Communication" },
    { name: "Emma Williams", text: "Same-day service when everywhere else was fully booked. Arrived on time, diagnosed the problem quickly, and had it fixed within a couple of hours. Highly recommend!", rating: 5, highlight: "Same-Day Fix" },
    { name: "Michael Brown", text: "Very professional and knowledgeable. Solved an electrical issue that two other garages couldn't figure out. Reasonable price too.", rating: 5, highlight: "Expert Knowledge" },
    { name: "Lisa Taylor", text: "Fantastic mobile mechanic service. Came to my workplace and sorted my car while I was in meetings. Couldn't be more convenient.", rating: 5, highlight: "Convenient Service" },
    { name: "Robert Davies", text: "Trustworthy and reliable. Been using Fazeli's for all my cars now. Always fair, always professional, always gets the job done right.", rating: 5, highlight: "Reliable" },
    { name: "Jennifer Clark", text: "Emergency breakdown on the motorway at midnight. Was with me in under 40 minutes and got me home safely. Can't thank them enough!", rating: 5, highlight: "Emergency Response" },
    { name: "Andrew Wilson", text: "Great experience from start to finish. Easy to book, arrived on time, friendly service, and fair price. What more could you ask for?", rating: 5, highlight: "Professional" },
  ];
  return (
    <section id="reviews" className="section bg-[#FBF6E9] tools-pattern scroll-mt-20" data-testid="reviews-section">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#4F7A5F] mb-4">Customer Reviews</p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-normal uppercase text-[#1E3F2D] mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            What Our Customers Say
          </h2>
          <div className="flex items-center justify-center gap-3 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="star-gold fill-current" size={28} />
            ))}
          </div>
          <p className="text-3xl font-bold text-[#1E3F2D] mb-2">4.8 out of 5</p>
          <p className="text-xl text-[#6B7280] mb-8">Based on 190+ customer reviews</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="btn-outline inline-flex items-center justify-center gap-2" data-testid="reviews-hero-google-btn">
              <GoogleIcon size={20} />
              Read Reviews on Google
            </a>
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="reviews-hero-write-btn">
              <Star size={20} className="fill-current" />
              Leave a Review
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review, index) => (
            <div key={index} className="testimonial-card" data-testid={`review-card-${index}`}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex gap-1">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="star-gold fill-current" size={18} />
                  ))}
                </div>
                <span className="text-xs font-medium uppercase tracking-wider text-[#1E3F2D] bg-[#FCF4DD] border border-[#EFE4BF] px-3 py-1 rounded">{review.highlight}</span>
              </div>
              <p className="text-[#4B5563] mb-4">"{review.text}"</p>
              <p className="font-semibold text-[#111827]">{review.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", vehicle_issue: "", location: "" });
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const lines = [
      "Hi Fazeli's Autos, I'd like a quote.",
      "",
      `Name: ${formData.name}`,
      `Phone: ${formData.phone}`,
      formData.email ? `Email: ${formData.email}` : null,
      `Location: ${formData.location}`,
      `Vehicle issue: ${formData.vehicle_issue}`,
    ].filter(Boolean).join("\n");
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines)}`, "_blank");
    // Save the lead to the database in the background (backup)
    axios.post(`${API}/contact`, formData).catch((err) => console.error("Lead save failed:", err));
    toast.success("Opening WhatsApp with your details…");
    setFormData({ name: "", phone: "", email: "", vehicle_issue: "", location: "" });
  };

  const faqs = [
    { q: "Do you come to my location?", a: "Yes! We're a fully mobile service. We come to your home, workplace, or roadside location anywhere in our service area." },
    { q: "Do you offer same-day service?", a: "Yes, most repairs are completed the same day. For emergency breakdowns, we aim to reach you as quickly as possible." },
    { q: "Are you available 24 hours?", a: "Absolutely. We're available 24/7, 365 days a year. Car problems don't follow a schedule, and neither do we." },
    { q: "How do I get a quote?", a: "Call us or fill out the contact form with details of your vehicle issue. We'll provide a free, no-obligation quote before any work begins." },
    { q: "What areas do you cover?", a: "We cover the North West of England and North Wales including: Liverpool, Manchester, Greater Manchester, Wigan, Warrington, St Helens, Chester, Chorley, Runcorn, Wirral, Southport, Ellesmere Port, Widnes, Skelmersdale, Flint, Rhyl, Mold, Deeside, Preston, Blackburn, Burnley, Oldham, Glossop, Crewe, Wrexham and Colwyn Bay." },
    { q: "Do you provide warranties on work?", a: "Yes, all our work comes with a warranty. We stand behind our repairs and your satisfaction is guaranteed." },
  ];

  return (
    <>
      {/* Contact */}
      <section id="contact" className="section bg-white scroll-mt-20" data-testid="contact-section">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12">
            <div className="lg:w-1/2">
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#4F7A5F] mb-4">Contact Us</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-normal uppercase text-[#1E3F2D] mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                Get In Touch
              </h2>
              <p className="text-lg text-[#4B5563] mb-8">
                Need help with your vehicle? Contact us for a free quote or emergency assistance. We're available 24/7.
              </p>
              <div className="space-y-6">
                <a href={`tel:${PHONE_NUMBER}`} className="flex items-center gap-4 text-xl text-[#111827] hover:text-[#1E3F2D] transition-colors" data-testid="contact-phone">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#1E3F2D] text-white rounded-lg"><Phone size={24} /></div>
                  <div>
                    <p className="text-sm text-[#6B7280] uppercase tracking-wider">Call Us</p>
                    <p className="font-bold">{PHONE_NUMBER}</p>
                  </div>
                </a>
                <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I need help with my vehicle.`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 text-xl text-[#111827] hover:text-[#25D366] transition-colors" data-testid="contact-whatsapp">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#25D366] text-white rounded-lg"><MessageCircle size={24} /></div>
                  <div>
                    <p className="text-sm text-[#6B7280] uppercase tracking-wider">WhatsApp</p>
                    <p className="font-bold">Send a Message</p>
                  </div>
                </a>
                <div className="flex items-center gap-4 text-xl text-[#111827]">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#DCE3DB] text-[#1E3F2D] rounded-lg"><Clock size={24} /></div>
                  <div>
                    <p className="text-sm text-[#6B7280] uppercase tracking-wider">Availability</p>
                    <p className="font-bold">Open 24 Hours</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xl text-[#111827]">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#DCE3DB] text-[#1E3F2D] rounded-lg"><MapPin size={24} /></div>
                  <div>
                    <p className="text-sm text-[#6B7280] uppercase tracking-wider">Service Area</p>
                    <p className="font-bold">North West England & North Wales</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:w-1/2">
              <div className="bg-[#FBF6E9] border border-[#EFE4BF] rounded-lg p-8 shadow-sm">
                <h3 className="text-2xl font-bold uppercase mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Request a Quote</h3>
                <form onSubmit={handleSubmit} className="contact-form space-y-4" data-testid="contact-form">
                  <input type="text" name="name" placeholder="Your Name *" value={formData.name} onChange={handleChange} required data-testid="input-name" />
                  <input type="tel" name="phone" placeholder="Phone Number *" value={formData.phone} onChange={handleChange} required data-testid="input-phone" />
                  <input type="email" name="email" placeholder="Email (Optional)" value={formData.email} onChange={handleChange} data-testid="input-email" />
                  <textarea name="vehicle_issue" placeholder="Describe your vehicle issue *" rows={4} value={formData.vehicle_issue} onChange={handleChange} required data-testid="input-issue" />
                  <input type="text" name="location" placeholder="Your Location *" value={formData.location} onChange={handleChange} required data-testid="input-location" />
                  <button type="submit" className="whatsapp-btn w-full flex items-center justify-center gap-2" data-testid="submit-btn">
                    <MessageCircle size={20} />
                    Send via WhatsApp
                  </button>
                </form>
                <p className="text-sm text-[#6B7280] mt-4 text-center">We'll reply on WhatsApp — 24/7 emergency service available</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-[#F2F3F0] tools-pattern" data-testid="faq-section">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#4F7A5F] mb-4">FAQ</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-normal uppercase text-[#1E3F2D]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Frequently Asked Questions
            </h2>
          </div>
          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item" data-testid={`faq-${index}`}>
                <button className="faq-question w-full text-left" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                  <span className="text-lg font-semibold text-[#111827]">{faq.q}</span>
                  <ChevronDown className={`text-[#1E3F2D] transition-transform ${openFaq === index ? "rotate-180" : ""}`} size={24} />
                </button>
                <div className={`faq-answer ${openFaq === index ? "open" : ""}`}>
                  <p className="text-[#4B5563]">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section bg-white" data-testid="service-areas">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#4F7A5F] mb-4">Coverage</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-normal uppercase text-[#1E3F2D]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Areas We Cover
            </h2>
          </div>
          <div className="map-wrapper mb-10" data-testid="service-area-map-wrapper">
            <ServiceAreaMap />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              "Liverpool", "Manchester", "Greater Manchester", "Wigan", "Warrington", "St Helens",
              "Chester", "Chorley", "Runcorn", "Wirral", "Southport", "Ellesmere Port",
              "Widnes", "Skelmersdale", "Flint", "Rhyl", "Mold", "Deeside",
              "Preston", "Blackburn", "Burnley", "Oldham", "Glossop", "Crewe",
              "Wrexham", "Colwyn Bay"
            ].map((area, index) => (
              <div key={index} className="bg-[#FAFAF8] border border-[#E5E7E6] rounded px-4 py-3 text-center hover:border-[#1E3F2D] hover:bg-[#DCE3DB] transition-colors" data-testid={`area-${index}`}>
                <span className="text-[#374151] text-sm font-medium">{area}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-[#6B7280] mt-8">
            Not sure if we cover your area? <a href={`tel:${PHONE_NUMBER}`} className="text-[#1E3F2D] font-semibold hover:underline">Give us a call</a> and we'll let you know!
          </p>
        </div>
      </section>
    </>
  );
};

const CTASection = () => (
  <section className="section bg-[#FBF3DC] relative overflow-hidden" data-testid="cta-section">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#8DA38A]/20 via-transparent to-transparent" />
    <div className="container mx-auto px-4 lg:px-8 relative z-10">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold tracking-normal uppercase text-[#1E3F2D] mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          Need Emergency Help?
        </h2>
        <p className="text-xl text-[#4B5563] mb-8">
          Stranded with car trouble? We're available 24/7 and can be with you fast.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href={`tel:${PHONE_NUMBER}`} className="btn-primary flex items-center justify-center gap-2 animate-pulse-glow" data-testid="cta-call-btn">
            <Phone size={20} />
            Call {PHONE_NUMBER}
          </a>
          <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I need help with my vehicle.`} target="_blank" rel="noopener noreferrer" className="whatsapp-btn flex items-center justify-center gap-2" data-testid="cta-whatsapp-btn">
            <MessageCircle size={20} />
            WhatsApp Us
          </a>
        </div>
      </div>
    </div>
  </section>
);

// Main App Component - Single Page Landing
function App() {
  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Toaster position="top-right" richColors />
      <Navbar />
      <main>
        <HeroSection />
        <BenefitsSection />
        <ServicesSection />
        <AboutSection />
        <GallerySection />
        <ReviewsSection />
        <ContactSection />
        <CTASection />
      </main>
      <Footer />
      <StickyButtons />
    </div>
  );
}

export default App;
